"use client";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { Monitor, Smartphone, Check } from "lucide-react";

interface FormatSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string[];
  onValueChange?: (value: string[]) => void;
  disabled?: boolean;
}

const formats = [
  { 
    value: "16:9", 
    label: "createVideo.horizontal", 
    icon: Monitor,
    description: "Best for YouTube and TV"
  },
  { 
    value: "9:16", 
    label: "createVideo.vertical", 
    icon: Smartphone,
    description: "Best for Instagram and TikTok"
  },
];

function FormatSelector({
  value = ["16:9"],
  onValueChange,
  disabled = false,
  className,
  ...props
}: FormatSelectorProps) {
  const { t } = useTranslation();

  const handleToggle = (formatValue: string) => {
    if (disabled) return;
    
    const isSelected = value.includes(formatValue);
    
    if (isSelected) {
      if (value.length <= 1) return;
      onValueChange?.(value.filter(v => v !== formatValue));
    } else {
      if (value.length >= 2) return;
      onValueChange?.([...value, formatValue]);
    }
  };

  const isMaxReached = value.length >= 2;

  return (
    <div className={cn("space-y-6 2k:space-y-10 4k:space-y-16", className)} {...props}>
      <div className="flex items-center justify-between">
        <Label className="font-h3 text-xl 2k:text-4xl 4k:text-6xl text-black">
          {t("formatSelector.label")}
        </Label>
        <div className="flex items-center gap-2 px-3 py-1 bg-black/5 rounded-full">
          <span className="text-xs font-bold 2k:text-xl 4k:text-3xl text-black">
            {value.length} / 2
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 2k:gap-8 4k:gap-12">
        {formats.map((format) => {
          const isSelected = value.includes(format.value);
          const Icon = format.icon;
          const isDisabled = disabled || (!isSelected && isMaxReached);
          
          return (
            <button
              key={format.value}
              type="button"
              onClick={() => handleToggle(format.value)}
              disabled={isDisabled}
              className={cn(
                "relative flex flex-col items-start p-6 2k:p-12 4k:p-18 rounded-3xl border-2 transition-all text-left group cursor-pointer",
                isSelected 
                  ? "bg-black border-black shadow-2xl shadow-black/20" 
                  : "bg-white border-black/5 hover:border-black/20 hover:shadow-lg",
                isDisabled && !isSelected && "opacity-40 grayscale cursor-not-allowed"
              )}
            >
              {/* Checkmark Badge */}
              <div className={cn(
                "absolute top-4 right-4 2k:top-8 2k:right-8 w-6 h-6 2k:size-10 4k:size-16 rounded-full flex items-center justify-center transition-all",
                isSelected ? "bg-white text-black scale-100" : "bg-black/5 text-transparent scale-0"
              )}>
                <Check className="size-4 2k:size-6 4k:size-10 stroke-[3]" />
              </div>

              {/* Icon Container */}
              <div className={cn(
                "w-12 h-12 2k:size-20 4k:size-30 rounded-2xl flex items-center justify-center mb-6 transition-all",
                isSelected ? "bg-white/10 text-white" : "bg-black/5 text-black"
              )}>
                <Icon className="size-6 2k:size-10 4k:size-16" />
              </div>

              {/* Text Content */}
              <div className="space-y-1 2k:space-y-3">
                <p className={cn(
                  "font-bold text-lg 2k:text-3xl 4k:text-5xl",
                  isSelected ? "text-white" : "text-black"
                )}>
                  {t(format.label)}
                </p>
                <p className={cn(
                  "text-xs 2k:text-xl 4k:text-3xl font-medium",
                  isSelected ? "text-white/60" : "text-muted-foreground"
                )}>
                  {format.description}
                </p>
              </div>

              {/* Aspect Ratio Tag */}
              <div className={cn(
                "mt-4 px-2 py-0.5 rounded text-[10px] 2k:text-lg 4k:text-2xl font-bold uppercase tracking-widest",
                isSelected ? "bg-white/10 text-white/80" : "bg-black/5 text-black/40"
              )}>
                {format.value}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { FormatSelector };